import React, { useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useLabels, useRequestData } from 'wsm-common-data';
import { setClassNames } from 'ddc-classnames-js';
import { trackEvent } from 'ddc-track-event';
import { isBrowser } from 'ws-scripts/modules/environment';
import { selectIsTabbedLayout } from '../../utilities/layout';
import { useDeviceType, useFooterCTAs } from '../../hooks';
import { useCompareVehiclesList } from '../../hooks/useCompareVehicles';
import {
	addCompareVehicle,
	removeCompareVehicle
} from '../../features/compare';
import { getVehicleObj } from '../../utilities/vehicleTracking';
import { setCompareTriggerElement } from '../../utilities/compareTriggerRef';
import { getCompareBarDismissId } from '../../utilities/compare';
import { COMPARE_BUTTON_VARIANTS } from '../../global-constants';
import './CompareButton.scss';

const CompareButton = ({
	uuid,
	title,
	variant = COMPARE_BUTTON_VARIANTS.FOOTER
}) => {
	const dispatch = useDispatch();
	const labels = useLabels();
	const { widgetName, windowId } = useRequestData();
	const compareVehicles = useCompareVehiclesList();
	const selected = compareVehicles.includes(uuid);
	const compareCount = compareVehicles?.length || 0;
	const barVisible = useSelector(
		(state) => state.compareVehicles.compareBarVisible
	);

	// Footer variant needs layout context
	const isDesktop = useDeviceType();
	const { footerHasMyCars } = useFooterCTAs();
	const isTabbedLayout = useSelector((state) => selectIsTabbedLayout(state));
	const isInline = !isTabbedLayout;

	const dismissBtnId = getCompareBarDismissId(windowId);
	const compareBtnRef = useRef(null);

	const wasEverSelected = useRef(selected);
	if (selected) {
		wasEverSelected.current = true;
	}

	const isFooter = variant === COMPARE_BUTTON_VARIANTS.FOOTER;

	// Accessible name
	const vehicleTitle = Array.isArray(title) ? title.join(' ') : title || '';
	const isOverImageSelected = !isFooter && selected;
	let ariaLabel;
	if (vehicleTitle) {
		const labelToken = isOverImageSelected
			? 'REMOVE_THIS_X'
			: 'COMPARE_THIS_X';
		ariaLabel = labels.get(labelToken).replace('{item}', vehicleTitle);
	} else {
		ariaLabel = isOverImageSelected
			? labels.get('REMOVE')
			: labels.get('COMPARE');
	}

	const handleSkipToCompare = useCallback(
		(e) => {
			e.preventDefault();
			if (isBrowser) {
				// Set trigger ref to this card's Compare button for focus return
				if (compareBtnRef.current) {
					setCompareTriggerElement(compareBtnRef.current);
				}

				const dismissBtn = document.getElementById(dismissBtnId);
				if (dismissBtn) {
					// TODO: Fix bug in which a paginated SRP will automatically go back to page 1 when an in-page link is activated.
					// To avoid the bug, we are manually focusing the element instead of using the href.
					dismissBtn.focus();
				}
			}
		},
		[dismissBtnId]
	);

	const handleClick = (e) => {
		const newSelected = !selected;
		const vehicleObj = getVehicleObj(uuid);

		if (isBrowser && window.DDC?.pushToGtmDataLayer) {
			const eventData = {
				isSRP: true,
				checked: newSelected,
				element: e.target,
				...vehicleObj
			};
			delete eventData.condition;
			window.DDC.pushToGtmDataLayer({
				event: 'ddc.link.vehicleCompareCheck',
				eventData
			});
		}

		dispatch(
			newSelected ? addCompareVehicle(uuid) : removeCompareVehicle(uuid)
		);
		trackEvent(
			widgetName,
			windowId,
			newSelected
				? {
						element: 'Compare selection',
						result: 'Vehicle selected for comparison',
						vehicle: vehicleObj
				  }
				: {
						element: 'Deselect comparison selection link',
						result: 'Vehicle removed from comparison'
				  }
		);
	};

	// Variant-specific classes computed upfront
	const containerClasses = isFooter
		? ['d-flex', 'align-items-center', 'justify-content-center']
		: ['compare-over-image-container', 'position-absolute'];

	const buttonClasses = isFooter
		? [
				'modern-my-cars-compare-label',
				'border-0',
				'bg-none',
				'p-0',
				selected ? 'text-primary-700 text-decoration-none' : ''
		  ]
		: [
				'compare-over-image',
				'transition-duration-xl',
				'text-decoration-none',
				'overflow-hidden',
				'd-inline-flex',
				'align-items-center',
				'flex-row-reverse',
				selected
					? 'compare-over-image-selected'
					: 'compare-over-image-unselected'
		  ];

	const renderButtonContent = () => {
		if (isFooter) {
			const icon = selected
				? 'ddc-icon-checkmark2'
				: 'ddc-icon-checkmark2-outline';

			return (
				<span
					className={setClassNames([
						'd-flex',
						isInline || !footerHasMyCars
							? 'flex-row'
							: 'flex-column',
						'align-items-center',
						'py-1',
						'px-1'
					])}
				>
					<i
						data-testid="modern-my-cars-compare-label-icon"
						className={setClassNames([
							'ddc-icon',
							icon,
							isDesktop ? 'ddc-icon-size-small' : ''
						])}
						aria-hidden="true"
					/>
					<span
						className={setClassNames([
							isInline || !footerHasMyCars ? 'ml-3' : '',
							'label-text',
							'font-weight-normal'
						])}
					>
						{labels.get('COMPARE')}
					</span>
				</span>
			);
		}

		// Over-image variant
		const icon = selected ? 'ddc-icon-check' : 'ddc-icon-expand';
		const label = selected ? labels.get('REMOVE') : labels.get('COMPARE');

		return (
			<>
				<span
					className={setClassNames([
						'compare-over-image-icon',
						'ddc-icon',
						'icon-size-3',
						icon
					])}
					aria-hidden="true"
				/>
				<span
					className={setClassNames([
						'compare-over-image-text',
						'ddc-font-size-xsmall',
						'px-2',
						'text-nowrap'
					])}
				>
					{label}
				</span>
			</>
		);
	};

	const skipLinkText = labels.get('SKIP_TO_COMPARE_BAR');

	return (
		<>
			<div className={setClassNames(containerClasses)}>
				{/* Only footer variant uses aria-pressed; over-image variant's changing label already indicates state (per W3C ARIA APG) */}
				<button
					type="button"
					className={setClassNames(buttonClasses)}
					id={`${uuid}-compare`}
					aria-label={ariaLabel}
					{...(isFooter && { 'aria-pressed': selected })}
					onClick={handleClick}
					ref={compareBtnRef}
				>
					{renderButtonContent()}
				</button>
			</div>
			{wasEverSelected.current && compareCount > 0 && barVisible && (
				<a
					href={`#${dismissBtnId}`}
					className="skip-link"
					onClick={handleSkipToCompare}
				>
					{skipLinkText}
				</a>
			)}
		</>
	);
};

CompareButton.propTypes = {
	uuid: PropTypes.string.isRequired,
	title: PropTypes.oneOfType([
		PropTypes.string,
		PropTypes.arrayOf(PropTypes.string)
	]),
	variant: PropTypes.oneOf([
		COMPARE_BUTTON_VARIANTS.FOOTER,
		COMPARE_BUTTON_VARIANTS.OVER_IMAGE
	])
};

export default CompareButton;
